import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Film, Clapperboard, Loader2, Check, ArrowLeft, ArrowRight, Info, AlertTriangle,
  Sparkles, Package, ShieldAlert, Server, Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOVIE_MODES = [
  { id: "insert_update", title: "Inserir e Atualizar", tone: "warn",
    note: "Essa opção faz sua lista DEMORAR mais pra importar. Tenha paciência.",
    bullets: [
      "As categorias de filmes não serão excluídas; se não existir, será criada.",
      "O script tenta achar o filme pelo TMDB ID ou pelo nome; se não achar, cria.",
      "O filme é adicionado nos bouquets se ainda não estiver.",
      "Se o filme já existir, a categoria não é alterada.",
    ] },
  { id: "insert_only", title: "Inserir, Não Atualizar", tone: "ok", recommended: true,
    note: "Recomendamos essa opção.",
    bullets: ["Mesmas regras da atualização, porém só insere se não existir. Não atualiza nada."] },
  { id: "delete_all", title: "Excluir tudo", tone: "danger",
    bullets: [
      "TODAS as categorias de filmes serão excluídas e recriadas a partir do M3U.",
      "Todos os filmes serão excluídos.",
      "Depois de excluir, o script muda para 'Inserir, Não Atualizar' automaticamente.",
    ] },
];

const SERIES_MODES = [
  { id: "insert_update", title: "Inserir e Atualizar", tone: "warn", recommended: true,
    note: "Recomendamos essa opção, mas ela faz sua lista DEMORAR mais pra importar.",
    bullets: [
      "As categorias de séries não serão excluídas; se não existir, será criada.",
      "O script tenta achar a série pelo TMDB ID ou pelo nome; se não achar, cria.",
      "Se a série for encontrada, novas temporadas e episódios são adicionados.",
      "A série é adicionada nos bouquets se ainda não estiver.",
    ] },
  { id: "insert_only", title: "Inserir, Não Atualizar", tone: "ok",
    bullets: ["Só insere se não existir. Não atualiza nada, nem adiciona novos episódios."] },
  { id: "delete_all", title: "Excluir tudo", tone: "danger",
    bullets: [
      "TODAS as categorias de séries serão excluídas e recriadas a partir do M3U.",
      "Todas as séries serão excluídas.",
      "Depois, o script muda para 'Inserir e Atualizar' automaticamente.",
    ] },
];

const MOVIE_OPTIONS = [
  { key: "use_dns", label: "Usar o DNS do stream para verificar se o filme já existe",
    desc: "Útil se você tem conteúdo próprio e alugado no mesmo servidor. Se não sabe pra que serve, não marque." },
  { key: "merge_sources", label: "Combinar Fornecedores (recomendado)", highlight: true,
    desc: "Use mais de um fornecedor: adicione vários links. O 1º fornecedor sobe TODOS os filmes. Nos fornecedores seguintes, os filmes que FALTAM são inseridos; os que JÁ existem NÃO sobem de novo (o sistema só avisa que já existe). Sem duplicar." },
  { key: "strip_accents", label: "Cadastrar filmes sem acento (facilita a busca)",
    desc: "Remove os acentos dos títulos ao cadastrar. Assim, se o cliente pesquisar sem acento (ex: 'Ilusao' em vez de 'Ilusão'), o filme continua aparecendo na busca do app." },
  { key: "ignore_title_url", label: "Ignorar título e usar apenas a URL da fonte para verificar duplicatas",
    desc: "O sistema usa só a URL da fonte para verificar se o filme já existe." },
  { key: "delete_dupes_before", label: "Excluir filmes duplicados antes da sincronização", danger: true,
    desc: "Antes de sincronizar, varre o servidor e FUNDE filmes duplicados usando identificação inteligente (TMDB id + título original + ano), não só nome exato — pega até capas/nomes diferentes do mesmo filme, mantendo o cadastro mais antigo. Legendado [L] fica separado. Atenção: pode remover conteúdo próprio ou alugado." },
  { key: "remove_orphans", label: "Remover filmes órfãos após a sincronização (somente IPTV)",
    desc: "Remove filmes que não estão no M3U. Mantém apenas os filmes da sua fonte M3U atual." },
  { key: "dedup_cross_category", label: "Não duplicar o mesmo filme entre categorias",
    desc: "Cadastra cada filme UMA única vez, mesmo que ele apareça em várias categorias do fornecedor (ex: Ação, Lançamentos e Coletânea). Fica na primeira categoria importada. Reduz o volume no servidor. As versões 4K e Legendado continuam separadas." },
  { key: "skip_tmdb", label: "Pular filmes com ID TMDB existente",
    desc: "Pula filmes que já existem no servidor com o mesmo ID TMDB." },
];

const SERIES_OPTIONS = [
  { key: "use_dns", label: "Usar o DNS do stream para verificar se a série já existe",
    desc: "Útil se você tem conteúdo próprio e alugado no mesmo servidor. Se não sabe, não marque." },
  { key: "merge_sources", label: "Combinar Fornecedores (recomendado)", highlight: true,
    desc: "Use mais de um fornecedor: adicione vários links. O 1º fornecedor sobe TODAS as séries. Nos fornecedores seguintes, as séries que faltam são inseridas e as séries já existentes que estiverem desatualizadas são COMPLETADAS: as temporadas e episódios que faltam (ex.: 'A Casa do Dragão' T3E5 que só saiu no fornecedor 2) são adicionados. O que já existe não sobe de novo; o sistema avisa o que foi feito." },
  { key: "strip_accents", label: "Cadastrar séries sem acento (facilita a busca)",
    desc: "Remove os acentos dos títulos ao cadastrar. Assim, se o cliente pesquisar sem acento (ex: 'Ilusao' em vez de 'Ilusão'), a série continua aparecendo na busca do app." },
  { key: "delete_dupes_before", label: "Excluir séries duplicadas antes da sincronização", danger: true,
    desc: "Antes de sincronizar, varre o servidor e FUNDE séries duplicadas usando identificação inteligente (TMDB id + título original + ano), não só nome exato — reconhece variações como 'Breaking Bad', 'Breaking Bad: A Química do Mal' e 'Breaking.Bad' como a MESMA série, mantendo a com mais episódios. Legendado [L] fica separado." },
  { key: "remove_orphans", label: "Remover séries órfãs após a sincronização (somente IPTV)",
    desc: "Remove séries que não estão no M3U. Mantém apenas as da sua fonte M3U atual." },
  { key: "skip_tmdb", label: "Pular séries com ID TMDB existente",
    desc: "Pula séries que já existem no servidor com o mesmo ID TMDB." },
  { key: "delete_dupe_episodes", label: "Excluir episódios duplicados após a sincronização", experimental: true,
    desc: "EXPERIMENTAL: faça backup antes. Mantém apenas um episódio por URL de origem." },
];

const TONE = {
  ok: "border-green-300 bg-green-50 text-green-800",
  warn: "border-amber-300 bg-amber-50 text-amber-800",
  danger: "border-red-300 bg-red-50 text-red-800",
};

const TMDB_ACCENT_NOTE =
  "Desativado porque o TMDB está ativo: usamos o nome OFICIAL do TMDB (com acentos). Remover acentos corromperia os títulos e atrapalharia a identificação/capas. A busca no app já funciona sem acento pelo próprio painel.";

const RadioCard = ({ mode, selected, onSelect }) => (
  <button type="button" onClick={() => onSelect(mode.id)} data-testid={`mode-${mode.id}`}
    className={`w-full rounded-xl border-2 p-4 text-left transition-all ${
      selected ? "border-green-500 bg-green-50/60 shadow-sm" : "border-slate-200 bg-white hover:border-green-300"
    }`}>
    <div className="flex items-center gap-2">
      <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${selected ? "border-green-600 bg-green-600" : "border-slate-300"}`}>
        {selected && <span className="h-2 w-2 rounded-full bg-white" />}
      </span>
      <span className="font-display font-bold text-slate-800">{mode.title}</span>
      {mode.recommended && <span className="rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-semibold text-white">Recomendado</span>}
    </div>
    {mode.note && <div className={`mt-2 rounded-lg border px-3 py-2 text-xs ${TONE[mode.tone] || TONE.warn}`}>{mode.note}</div>}
    <ul className="mt-2 space-y-1 pl-1 text-xs text-slate-500">
      {mode.bullets.map((b, i) => <li key={i} className="flex gap-1.5"><span className="text-slate-300">•</span>{b}</li>)}
    </ul>
  </button>
);

const CheckRow = ({ opt, checked, onToggle, section, disabled, note }) => (
  <button type="button" onClick={() => !disabled && onToggle(opt.key)} disabled={disabled}
    data-testid={`opt-${section}-${opt.key}`}
    className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
      disabled ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white hover:border-green-300"
    }`}>
    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${
      checked ? "border-green-600 bg-green-600 text-white" : "border-slate-300"
    }`}>{checked && <Check className="h-3.5 w-3.5" />}</span>
    <span>
      <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
        {opt.label}
        {opt.experimental && <span className="rounded bg-fuchsia-100 px-1.5 py-0.5 text-[10px] font-bold text-fuchsia-700">EXPERIMENTAL</span>}
        {opt.highlight && <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-bold text-green-700">MULTIFORNECEDOR</span>}
        {disabled && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">DESATIVADO</span>}
      </span>
      <span className={`mt-0.5 block text-xs ${opt.danger ? "text-red-600" : "text-slate-500"}`}>{opt.desc}</span>
      {note && (
        <span className="mt-1.5 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700" data-testid={`opt-${section}-${opt.key}-note`}>
          <Info className="mt-0.5 h-3 w-3 shrink-0" />{note}
        </span>
      )}
    </span>
  </button>
);

const BouquetPicker = ({ title, subtitle, bouquets, selected, onToggle, testid, warn }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 font-display font-bold text-slate-800">
      <Package className="h-4 w-4 text-green-600" /> {title}
    </div>
    <div className={`mt-2 rounded-lg border px-3 py-2 text-xs ${warn ? "border-red-200 bg-red-50 text-red-700" : "border-sky-200 bg-sky-50 text-sky-800"}`}>
      {subtitle}
    </div>
    <p className="mt-3 text-xs font-medium uppercase tracking-wider text-slate-400">Escolha quantos quiser</p>
    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
      {bouquets.map((b) => {
        const on = selected.includes(String(b.id));
        return (
          <button key={b.id} type="button" onClick={() => onToggle(String(b.id))}
            data-testid={`${testid}-${b.id}`}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
              on ? "border-green-500 bg-green-50 text-green-800" : "border-slate-200 bg-white text-slate-600 hover:border-green-300"
            }`}>
            <span className={`flex h-4 w-4 items-center justify-center rounded border-2 ${on ? "border-green-600 bg-green-600 text-white" : "border-slate-300"}`}>
              {on && <Check className="h-3 w-3" />}
            </span>
            {b.name}
          </button>
        );
      })}
    </div>
  </div>
);

export default function Step2Config({ code, data, onBack, onNext }) {
  const t = useT();
  const saved = data?.sync_config || {};
  const sm = saved.movies || {};
  const ss = saved.series || {};

  const [bouquets, setBouquets] = useState([]);
  const [source, setSource] = useState(null);
  const [serverErr, setServerErr] = useState("");
  const [loadingB, setLoadingB] = useState(true);
  const [tmdbActive, setTmdbActive] = useState(false);
  const [serverIps, setServerIps] = useState([]);

  useEffect(() => {
    axios.get(`${API}/panel/${code}/server-ip`)
      .then((r) => setServerIps(r.data.ips && r.data.ips.length ? r.data.ips : (r.data.ip ? [r.data.ip] : [])))
      .catch(() => {});
  }, [code]);

  const [moviesMode, setMoviesMode] = useState(sm.mode || "insert_only");
  const [seriesMode, setSeriesMode] = useState(ss.mode || "insert_update");
  const [movieChecks, setMovieChecks] = useState(sm.options || {});
  const [seriesChecks, setSeriesChecks] = useState(ss.options || {});
  const [orphanCleanup, setOrphanCleanup] = useState(ss.orphan_cleanup || "report_only");

  const [bMovies, setBMovies] = useState(sm.bouquets || []);
  const [bMoviesAdult, setBMoviesAdult] = useState(sm.bouquets_adult || []);
  const [bSeries, setBSeries] = useState(ss.bouquets || []);
  const [bSeriesAdult, setBSeriesAdult] = useState(ss.bouquets_adult || []);
  const vf = saved.verify || {};
  const [verifyOn, setVerifyOn] = useState(!!vf.enabled);
  const [vspeed, setVspeed] = useState(vf.speed || "balanced");
  const [autoRepair, setAutoRepair] = useState(!!vf.auto_repair);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/panel/${code}/bouquets`)
      .then((r) => { setBouquets(r.data.bouquets || []); setSource(r.data.source); setTmdbActive(!!r.data.tmdb_active); setServerErr(r.data.server_ok ? "" : (r.data.server_error || "")); })
      .catch(() => { setBouquets([]); setServerErr("Não foi possível conectar ao seu servidor agora."); })
      .finally(() => setLoadingB(false));
  }, [code]);

  const toggleIn = (arr, setArr) => (id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const toggleCheck = (obj, setObj) => (key) => setObj({ ...obj, [key]: !obj[key] });

  const save = async () => {
    setSaving(true);
    // Com TMDB ativo, "remover acentos" fica desligado (nome oficial do TMDB).
    const cleanOpts = (obj) => (tmdbActive ? { ...obj, strip_accents: false } : obj);
    try {
      await axios.post(`${API}/panel/${code}/sync-config`, {
        movies: { mode: moviesMode, options: cleanOpts(movieChecks), bouquets: bMovies, bouquets_adult: bMoviesAdult },
        series: { mode: seriesMode, options: cleanOpts(seriesChecks), orphan_cleanup: orphanCleanup, bouquets: bSeries, bouquets_adult: bSeriesAdult },
        verify: { enabled: verifyOn, speed: vspeed, auto_repair: autoRepair },
      });
      toast.success("Configurações salvas! Agora informe sua fonte M3U.");
      onNext && onNext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar as configurações.");
    } finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="mt-6 space-y-6" data-testid="panel-step2">

      <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span><b>ATENÇÃO:</b> leia atentamente cada opção. Marque somente o que você entende.</span>
      </div>

      {serverErr && (
        <div data-testid="bouquets-server-error" className="flex items-start gap-2 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>Não foi possível ler os bouquets do SEU servidor.</b> Mostrando exemplos abaixo.
            <span className="mt-1 block font-mono text-[11px] text-amber-600">{serverErr}</span>
            <span className="mt-1 block text-[12px]">Se aparecer "Access denied ... @IP", libere no MySQL do seu servidor o acesso desse IP (ou use host <b>%</b>) e o firewall na porta 3306.</span>
          </div>
        </div>
      )}

      {source && !serverErr && (
        <div className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-xs ${
          source === "server" ? "border-green-200 bg-green-50 text-green-700" : "border-amber-200 bg-amber-50 text-amber-700"
        }`} data-testid="bouquets-source">
          <Server className="h-4 w-4" />
          {source === "server"
            ? "Bouquets carregados diretamente do seu servidor."
            : "Não conseguimos ler os bouquets do seu servidor agora — mostrando exemplos. Salve a Etapa 1 com os dados corretos para carregar os reais."}
        </div>
      )}

      {/* FILMES */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 bg-green-600 px-5 py-3 font-display text-lg font-bold text-white">
          <Film className="h-5 w-5" /> {t("steps.s2movies")}
        </div>
        <div className="space-y-5 p-5">
          <div className="grid grid-cols-1 gap-3">
            {MOVIE_MODES.map((m) => <RadioCard key={m.id} mode={m} selected={moviesMode === m.id} onSelect={setMoviesMode} />)}
          </div>
          <div className="space-y-2">
            {MOVIE_OPTIONS.map((o) => {
              const dis = tmdbActive && o.key === "strip_accents";
              return <CheckRow key={o.key} opt={o} section="movies" disabled={dis} note={dis ? TMDB_ACCENT_NOTE : ""}
                checked={dis ? false : !!movieChecks[o.key]} onToggle={toggleCheck(movieChecks, setMovieChecks)} />;
            })}
          </div>
          {loadingB ? <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin text-green-600" /> Carregando bouquets do seu servidor…</div> : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <BouquetPicker testid="bq-movies" title="Bouquets dos FILMES" warn
                subtitle="Se não selecionar nenhum bouquet, as linhas não vão carregar nenhum conteúdo."
                bouquets={bouquets} selected={bMovies} onToggle={toggleIn(bMovies, setBMovies)} />
              <BouquetPicker testid="bq-movies-adult" title="Bouquets dos FILMES ADULTOS (opcional)"
                subtitle="Se um filme adulto for detectado e nenhum bouquet for selecionado, ele não será adicionado."
                bouquets={bouquets} selected={bMoviesAdult} onToggle={toggleIn(bMoviesAdult, setBMoviesAdult)} />
            </div>
          )}
        </div>
      </section>

      {/* SÉRIES */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 bg-slate-800 px-5 py-3 font-display text-lg font-bold text-white">
          <Clapperboard className="h-5 w-5" /> {t("steps.s2series")}
        </div>
        <div className="space-y-5 p-5">
          <div className="grid grid-cols-1 gap-3">
            {SERIES_MODES.map((m) => <RadioCard key={m.id} mode={m} selected={seriesMode === m.id} onSelect={setSeriesMode} />)}
          </div>
          <div className="space-y-2">
            {SERIES_OPTIONS.map((o) => {
              const dis = tmdbActive && o.key === "strip_accents";
              return <CheckRow key={o.key} opt={o} section="series" disabled={dis} note={dis ? TMDB_ACCENT_NOTE : ""}
                checked={dis ? false : !!seriesChecks[o.key]} onToggle={toggleCheck(seriesChecks, setSeriesChecks)} />;
            })}
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
              <ShieldAlert className="h-4 w-4 text-slate-500" /> Limpeza de registros órfãos de episódios
            </label>
            <select data-testid="orphan-cleanup" value={orphanCleanup} onChange={(e) => setOrphanCleanup(e.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-green-500 focus:outline-none">
              <option value="report_only">Apenas relatar registros órfãos de episódios e séries vazias</option>
              <option value="clean">Limpar registros órfãos de episódios e séries vazias</option>
            </select>
          </div>
          {loadingB ? <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin text-green-600" /> Carregando bouquets do seu servidor…</div> : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <BouquetPicker testid="bq-series" title="Bouquets das SÉRIES" warn
                subtitle="Se não selecionar nenhum bouquet, as linhas não vão carregar nenhum conteúdo."
                bouquets={bouquets} selected={bSeries} onToggle={toggleIn(bSeries, setBSeries)} />
              <BouquetPicker testid="bq-series-adult" title="Bouquets das SÉRIES ADULTAS (opcional)"
                subtitle="Se uma série adulta for detectada e nenhum bouquet for selecionado, ela não será adicionada."
                bouquets={bouquets} selected={bSeriesAdult} onToggle={toggleIn(bSeriesAdult, setBSeriesAdult)} />
            </div>
          )}
        </div>
      </section>

      {/* VERIFICADOR DE CONTEÚDO + FAILOVER */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="verify-section">
        <div className="flex items-center gap-2 bg-indigo-600 px-5 py-3 font-display text-lg font-bold text-white">
          <ShieldAlert className="h-5 w-5" /> Verificar conteúdo online (Beta)
        </div>
        <div className="space-y-3 p-5">
          <button type="button" onClick={() => setVerifyOn((v) => !v)} data-testid="verify-toggle"
            className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
              verifyOn ? "border-indigo-500 bg-indigo-50/60" : "border-slate-200 bg-white hover:border-indigo-300"
            }`}>
            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${
              verifyOn ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300"
            }`}>{verifyOn && <Check className="h-3.5 w-3.5" />}</span>
            <span>
              <span className="block text-sm font-medium text-slate-800">
                Verificar cada filme e episódio e trocar de fornecedor quando cair
              </span>
              <span className="mt-0.5 block text-xs text-slate-500">
                Durante a sincronização, checa se o link está no ar (302 = ok; 404/406 = removido; 401 = conta caiu).
                Se cair, tenta o próximo fornecedor que tenha o <b>mesmo</b> conteúdo (mesmo ano/TMDB e, em séries, mesma temporada/episódio).
                Se nenhum tiver, o item é listado em "Conteúdo Offline" na Etapa 5 (nada é removido sem você mandar).
              </span>
            </span>
          </button>
          {verifyOn && (
            <div className="space-y-2" data-testid="verify-speed">
              <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700" data-testid="verify-warning">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>Com essa opção <b>ligada</b>, o sync fica <b>mais lento</b> (checa item por item, com cache de 24h). Desligada, o sync roda normal. A <b>ordem dos fornecedores na Etapa 3 define a prioridade</b> (o 1º é o principal; os seguintes são reservas).</span>
              </div>
              <p className="text-xs font-medium text-slate-600">Velocidade da verificação:</p>
              <div className="grid grid-cols-3 gap-2">
                {[["safe", "🐢 Leve", "mais devagar, não pesa"], ["balanced", "⚖️ Equilibrado", "recomendado"], ["fast", "🐇 Rápido", "mais rápido, pesa mais"]].map(([v, l, d]) => (
                  <button key={v} type="button" data-testid={`verify-speed-${v}`} onClick={() => setVspeed(v)}
                    className={`rounded-lg border-2 p-2 text-center transition-all ${vspeed === v ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-300"}`}>
                    <div className="text-sm font-semibold text-slate-800">{l}</div>
                    <div className="text-[10px] text-slate-500">{d}</div>
                  </button>
                ))}
              </div>
              <label className="mt-1 flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 p-2.5" data-testid="auto-repair-row">
                <input type="checkbox" checked={autoRepair} onChange={(e) => setAutoRepair(e.target.checked)} data-testid="auto-repair-toggle" className="mt-0.5 h-4 w-4 accent-indigo-600" />
                <span className="text-xs text-slate-700">
                  <b>Consertar links automaticamente toda madrugada</b> — roda o "Consertar links quebrados" sozinho (~4h da manhã) para manter o acervo no ar sem você precisar clicar.
                </span>
              </label>
            </div>
          )}
        </div>
      </section>

      {serverIps.length > 0 && (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm" data-testid="verifier-ip-box">
          <div className="flex items-start justify-between gap-2">
            <span className="text-indigo-800">
              🔒 <b>IPs do verificador:</b> se você usa fornecedor com trava de IP, libere <b>todos</b> estes IPs nos "IPs Permitidos" do fornecedor (o IP pode mudar; deixe todos liberados).
            </span>
            <button type="button" data-testid="copy-verifier-ip"
              onClick={() => { navigator.clipboard.writeText(serverIps.join("\n")); toast.success("Todos os IPs copiados!"); }}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100">
              <Copy className="h-3.5 w-3.5" /> Copiar todos
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {serverIps.map((ip) => (
              <button key={ip} type="button"
                onClick={() => { navigator.clipboard.writeText(ip); toast.success("IP copiado!"); }}
                className="flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-white px-2.5 py-1 font-mono-code text-xs font-bold text-indigo-700 hover:bg-indigo-100">
                {ip} <Copy className="h-3 w-3" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
        <Button variant="outline" data-testid="step2-back" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> {t("common.back")}
        </Button>
        <Button data-testid="step2-save" onClick={save} disabled={saving}
          className="bg-green-600 py-6 text-base font-semibold text-white hover:bg-green-700 sm:py-2">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {t("common.saveAdvance")} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );
}
